import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/api';

interface OrgDirectoryRow {
  org_id: number;
  org_name: string;
  access_active: boolean;
  subscription_status: string;
  subscription_end_date: string | null;
  plan_name: string | null;
  subscription_id: number | null;
  digest_emails_enabled: boolean;
  token_balance?: number;
  user_count?: number;
}

interface OrgUser {
  id: number;
  email: string;
  role: string;
  is_active: boolean;
  title?: string | null;
  created_at?: string | null;
}

const SuperuserOrganizationsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orgs, setOrgs] = useState<OrgDirectoryRow[]>([]);
  const [savingOrgId, setSavingOrgId] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const [expandedOrgId, setExpandedOrgId] = useState<number | null>(null);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [userLimit, setUserLimit] = useState<{
    current_users?: number;
    max_users?: number;
  } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailMsg, setDetailMsg] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<'user' | 'admin'>('user');
  const [newTitle, setNewTitle] = useState('');
  const [addingUser, setAddingUser] = useState(false);
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);

  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpNotes, setTopUpNotes] = useState('');
  const [toppingUp, setToppingUp] = useState(false);

  const loadOrgs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.fetchSuperuserOrganizationsDirectory();
      if (res.success && res.organizations) {
        setOrgs(res.organizations as OrgDirectoryRow[]);
      } else {
        setError(res.error || 'Failed to load organizations');
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err?.response?.data?.error || err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  const loadOrgDetail = useCallback(async (orgId: number) => {
    setDetailLoading(true);
    setDetailError(null);
    setDetailMsg(null);
    setCreatedPassword(null);
    try {
      const res = await apiService.fetchSuperuserOrgUsers(orgId);
      if (!res.success) {
        setDetailError(res.error || 'Failed to load organization users');
        setOrgUsers([]);
        return;
      }
      setOrgUsers(res.users || []);
      setTokenBalance(res.token_balance ?? 0);
      setUserLimit(res.user_limit || null);
      setOrgs((prev) =>
        prev.map((o) =>
          o.org_id === orgId
            ? {
                ...o,
                token_balance: res.token_balance ?? o.token_balance,
                user_count: (res.users || []).length,
              }
            : o,
        ),
      );
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setDetailError(err?.response?.data?.error || err?.message || 'Failed to load users');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const toggleExpand = async (orgId: number) => {
    if (expandedOrgId === orgId) {
      setExpandedOrgId(null);
      return;
    }
    setExpandedOrgId(orgId);
    setNewEmail('');
    setNewRole('user');
    setNewTitle('');
    setTopUpAmount('');
    setTopUpNotes('');
    await loadOrgDetail(orgId);
  };

  const handleToggleDigest = async (orgId: number, next: boolean) => {
    setSavingOrgId(orgId);
    setMsg(null);
    try {
      const res = await apiService.patchOrgNewDataDigestSettings(orgId, next);
      if (res.success) {
        const enabled = res.new_data_digest_enabled !== false;
        setOrgs((prev) =>
          prev.map((o) => (o.org_id === orgId ? { ...o, digest_emails_enabled: enabled } : o)),
        );
      } else {
        setMsg(res.error || 'Update failed');
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setMsg(err?.response?.data?.error || err?.message || 'Update failed');
    } finally {
      setSavingOrgId(null);
    }
  };

  const handleAddUser = async (forceMove = false) => {
    if (!expandedOrgId || !newEmail.trim()) return;
    setAddingUser(true);
    setDetailError(null);
    setDetailMsg(null);
    setCreatedPassword(null);
    try {
      const res = await apiService.addSuperuserOrgUser(expandedOrgId, {
        email: newEmail.trim(),
        role: newRole,
        title: newTitle.trim() || undefined,
        force_move: forceMove,
      });
      if (res.success) {
        setDetailMsg(
          res.created
            ? `Created ${res.user?.email} and sent welcome email.`
            : `Moved ${res.user?.email} into this organization.`,
        );
        if (res.temp_password) setCreatedPassword(res.temp_password);
        setNewEmail('');
        setNewTitle('');
        await loadOrgDetail(expandedOrgId);
      } else if (res.needs_force_move) {
        const ok = window.confirm(
          `${res.error || 'User belongs to another org'}` +
            (res.current_org_name ? ` (${res.current_org_name}).` : '.') +
            ' Move them here anyway?',
        );
        if (ok) {
          await handleAddUser(true);
        } else {
          setDetailError(res.error || 'User belongs to another organization');
        }
      } else {
        setDetailError(res.error || 'Failed to add user');
      }
    } catch (e: unknown) {
      const err = e as {
        response?: {
          status?: number;
          data?: {
            error?: string;
            needs_force_move?: boolean;
            current_org_name?: string;
          };
        };
        message?: string;
      };
      const data = err?.response?.data;
      if (err?.response?.status === 409 || data?.needs_force_move) {
        const ok = window.confirm(
          `${data?.error || 'User belongs to another org'}` +
            (data?.current_org_name ? ` (${data.current_org_name}).` : '.') +
            ' Move them here anyway?',
        );
        if (ok) {
          await handleAddUser(true);
        } else {
          setDetailError(data?.error || 'User belongs to another organization');
        }
      } else {
        setDetailError(data?.error || err?.message || 'Failed to add user');
      }
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeactivateUser = async (user: OrgUser) => {
    if (!expandedOrgId) return;
    if (!window.confirm(`Set ${user.email} as inactive?`)) return;
    setDetailError(null);
    setDetailMsg(null);
    try {
      const res = await apiService.deactivateSuperuserOrgUser(expandedOrgId, user.id);
      if (res.success) {
        setDetailMsg(`Set ${user.email} as inactive.`);
        await loadOrgDetail(expandedOrgId);
      } else {
        setDetailError(res.error || 'Failed to deactivate user');
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setDetailError(err?.response?.data?.error || err?.message || 'Failed to deactivate user');
    }
  };

  const handleReactivateUser = async (user: OrgUser) => {
    if (!expandedOrgId) return;
    setDetailError(null);
    setDetailMsg(null);
    try {
      const res = await apiService.reactivateSuperuserOrgUser(expandedOrgId, user.id);
      if (res.success) {
        setDetailMsg(`Reactivated ${user.email}.`);
        await loadOrgDetail(expandedOrgId);
      } else {
        setDetailError(res.error || 'Failed to reactivate user');
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setDetailError(err?.response?.data?.error || err?.message || 'Failed to reactivate user');
    }
  };

  const handleTopUp = async () => {
    if (!expandedOrgId) return;
    const amount = Number.parseInt(topUpAmount, 10);
    if (!Number.isFinite(amount) || amount === 0) {
      setDetailError('Enter a non-zero token amount (negative allowed to debit).');
      return;
    }
    setToppingUp(true);
    setDetailError(null);
    setDetailMsg(null);
    try {
      const res = await apiService.topUpSuperuserOrgTokens(expandedOrgId, {
        amount,
        notes: topUpNotes.trim() || undefined,
      });
      if (res.success) {
        setDetailMsg(
          `${amount > 0 ? 'Added' : 'Removed'} ${Math.abs(amount)} tokens. New balance: ${res.token_balance}.`,
        );
        setTokenBalance(res.token_balance ?? tokenBalance + amount);
        setTopUpAmount('');
        setTopUpNotes('');
        setOrgs((prev) =>
          prev.map((o) =>
            o.org_id === expandedOrgId ? { ...o, token_balance: res.token_balance } : o,
          ),
        );
      } else {
        setDetailError(res.error || 'Token top-up failed');
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setDetailError(err?.response?.data?.error || err?.message || 'Token top-up failed');
    } finally {
      setToppingUp(false);
    }
  };

  const formatEndDate = (iso: string | null) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Organizations</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Manage org access, users, and token balances. Expand a row to add users, set them
            inactive, or top up tokens.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <a
            href="/superuser/create-org"
            className="inline-flex items-center justify-center px-3 py-2 rounded-xl text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
          >
            Create organization
          </a>
          <a
            href="/superuser/subscription-manager"
            className="inline-flex items-center justify-center px-3 py-2 rounded-xl text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Legacy subscription table
          </a>
        </div>
      </div>

      {loading && (
        <div className="bg-white rounded-2xl shadow-card border border-slate-200 p-6">
          <p className="text-slate-500 text-sm">Loading…</p>
        </div>
      )}

      {error && !loading && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-2xl p-4 text-sm">{error}</div>
      )}

      {!loading && !error && (
        <div className="bg-white rounded-2xl shadow-card border border-slate-200 p-6">
          {msg && <p className="text-sm text-rose-600 mb-3">{msg}</p>}
          {orgs.length === 0 ? (
            <p className="text-sm text-slate-500">No organizations yet.</p>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-xl">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2.5 px-3 w-10" />
                    <th className="py-2.5 px-3">Organization</th>
                    <th className="py-2.5 px-3">Access</th>
                    <th className="py-2.5 px-3">Plan</th>
                    <th className="py-2.5 px-3">End date</th>
                    <th className="py-2.5 px-3">Tokens</th>
                    <th className="py-2.5 px-3">Users</th>
                    <th className="py-2.5 px-3 w-[180px]">Digest emails</th>
                    <th className="py-2.5 px-3">Subscription</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((o) => {
                    const busy = savingOrgId === o.org_id;
                    const open = expandedOrgId === o.org_id;
                    return (
                      <React.Fragment key={o.org_id}>
                        <tr className="border-b border-slate-100">
                          <td className="py-2.5 px-3">
                            <button
                              type="button"
                              onClick={() => toggleExpand(o.org_id)}
                              className="text-indigo-600 font-bold text-xs hover:text-indigo-800"
                              aria-label={open ? 'Collapse' : 'Expand'}
                            >
                              {open ? '▾' : '▸'}
                            </button>
                          </td>
                          <td className="py-2.5 px-3 font-medium text-slate-800">
                            <button
                              type="button"
                              onClick={() => toggleExpand(o.org_id)}
                              className="text-left hover:text-indigo-700"
                            >
                              {o.org_name}
                            </button>
                          </td>
                          <td className="py-2.5 px-3">
                            {o.access_active ? (
                              <span className="text-emerald-700 text-xs font-semibold">Active</span>
                            ) : (
                              <span className="text-slate-600 text-xs font-semibold">Inactive</span>
                            )}
                            {o.subscription_status !== 'active' && o.subscription_status !== 'none' && (
                              <span className="block text-[11px] text-slate-400 mt-0.5 normal-case">
                                {o.subscription_status}
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-slate-600">{o.plan_name || '—'}</td>
                          <td className="py-2.5 px-3 text-slate-600 tabular-nums">
                            {formatEndDate(o.subscription_end_date)}
                          </td>
                          <td className="py-2.5 px-3 text-slate-700 tabular-nums font-semibold">
                            {(o.token_balance ?? 0).toLocaleString()}
                          </td>
                          <td className="py-2.5 px-3 text-slate-600 tabular-nums">
                            {o.user_count ?? '—'}
                          </td>
                          <td className="py-2.5 px-3">
                            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                className="rounded border-slate-300"
                                checked={o.digest_emails_enabled}
                                disabled={!o.subscription_id || busy}
                                onChange={(e) => handleToggleDigest(o.org_id, e.target.checked)}
                              />
                              <span className="text-slate-600">{busy ? 'Saving…' : 'Enabled'}</span>
                            </label>
                          </td>
                          <td className="py-2.5 px-3">
                            {o.subscription_id ? (
                              <a
                                href={`/superuser/subscription/edit/${o.subscription_id}`}
                                className="text-indigo-600 hover:text-indigo-800 font-semibold text-xs"
                              >
                                Edit
                              </a>
                            ) : (
                              <span className="text-slate-400 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                        {open && (
                          <tr className="bg-slate-50/80 border-b border-slate-200">
                            <td colSpan={9} className="px-4 py-4">
                              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                                  <div className="flex items-center justify-between gap-2">
                                    <h3 className="text-sm font-bold text-slate-900">Users</h3>
                                    <span className="text-xs text-slate-500">
                                      {userLimit
                                        ? `${userLimit.current_users ?? orgUsers.length}/${userLimit.max_users ?? '∞'} seats`
                                        : `${orgUsers.length} users`}
                                    </span>
                                  </div>
                                  {detailLoading ? (
                                    <p className="text-xs text-slate-500">Loading users…</p>
                                  ) : (
                                    <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg max-h-56 overflow-y-auto">
                                      {orgUsers.length === 0 ? (
                                        <li className="px-3 py-3 text-xs text-slate-500">
                                          No users in this org.
                                        </li>
                                      ) : (
                                        orgUsers.map((u) => (
                                          <li
                                            key={u.id}
                                            className="px-3 py-2 flex items-center justify-between gap-3"
                                          >
                                            <div className="min-w-0">
                                              <div className="text-sm font-medium text-slate-800 truncate">
                                                {u.email}
                                              </div>
                                              <div className="text-[11px] text-slate-500">
                                                {u.role}
                                                {u.title ? ` · ${u.title}` : ''}
                                                {!u.is_active ? ' · inactive' : ''}
                                              </div>
                                            </div>
                                            {u.is_active ? (
                                              <button
                                                type="button"
                                                onClick={() => handleDeactivateUser(u)}
                                                className="text-xs font-semibold text-rose-600 hover:text-rose-800 shrink-0"
                                              >
                                                Set inactive
                                              </button>
                                            ) : (
                                              <button
                                                type="button"
                                                onClick={() => handleReactivateUser(u)}
                                                className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 shrink-0"
                                              >
                                                Reactivate
                                              </button>
                                            )}
                                          </li>
                                        ))
                                      )}
                                    </ul>
                                  )}
                                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2">
                                    <input
                                      type="email"
                                      value={newEmail}
                                      onChange={(e) => setNewEmail(e.target.value)}
                                      placeholder="user@company.com"
                                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                    />
                                    <select
                                      value={newRole}
                                      onChange={(e) => setNewRole(e.target.value as 'user' | 'admin')}
                                      className="rounded-lg border border-slate-200 px-2 py-2 text-sm"
                                    >
                                      <option value="user">user</option>
                                      <option value="admin">admin</option>
                                    </select>
                                    <button
                                      type="button"
                                      disabled={addingUser || !newEmail.trim()}
                                      onClick={() => handleAddUser(false)}
                                      className="rounded-lg bg-indigo-600 text-white text-sm font-semibold px-3 py-2 hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                      {addingUser ? 'Adding…' : 'Add user'}
                                    </button>
                                  </div>
                                  <input
                                    type="text"
                                    value={newTitle}
                                    onChange={(e) => setNewTitle(e.target.value)}
                                    placeholder="Optional title"
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                  />
                                </div>

                                <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                                  <div className="flex items-center justify-between gap-2">
                                    <h3 className="text-sm font-bold text-slate-900">Token balance</h3>
                                    <span className="text-lg font-bold text-slate-900 tabular-nums">
                                      {tokenBalance.toLocaleString()}
                                    </span>
                                  </div>
                                  <p className="text-xs text-slate-500">
                                    Enter a positive amount to credit, or negative to debit.
                                  </p>
                                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                                    <input
                                      type="number"
                                      value={topUpAmount}
                                      onChange={(e) => setTopUpAmount(e.target.value)}
                                      placeholder="e.g. 100"
                                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                    />
                                    <button
                                      type="button"
                                      disabled={toppingUp || !topUpAmount.trim()}
                                      onClick={handleTopUp}
                                      className="rounded-lg bg-emerald-600 text-white text-sm font-semibold px-3 py-2 hover:bg-emerald-700 disabled:opacity-50"
                                    >
                                      {toppingUp ? 'Saving…' : 'Top up'}
                                    </button>
                                  </div>
                                  <input
                                    type="text"
                                    value={topUpNotes}
                                    onChange={(e) => setTopUpNotes(e.target.value)}
                                    placeholder="Optional notes (shown in ledger)"
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                  />
                                </div>
                              </div>

                              {detailError && (
                                <p className="text-sm text-rose-600 mt-3">{detailError}</p>
                              )}
                              {detailMsg && (
                                <p className="text-sm text-emerald-700 mt-3">{detailMsg}</p>
                              )}
                              {createdPassword && (
                                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                                  Temporary password (also emailed):{' '}
                                  <code className="font-mono font-semibold">{createdPassword}</code>
                                </p>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SuperuserOrganizationsPage;
